import React from 'react';
import './FoilCardImage.css';

interface FoilCardImageProps {
  src: string;
  alt: string;
  foil?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onError?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  loading?: 'lazy' | 'eager';
  borderRadius?: string;
}

/**
 * FoilCardImage - A reusable card image component with optional foil overlay
 *
 * Renders a card image with an optional holographic foil sheen. The sheen is a
 * pure-CSS gradient treatment (no SVG asset) defined in FoilCardImage.css. Because
 * the overlay uses `inset: 0` + `border-radius: inherit` and percentage-based
 * gradient geometry, it covers the exact card bounds and keeps a consistent look
 * at any card size (S/M/L tiles, the large Card Details view, and mobile).
 *
 * The treatment is tunable via CSS custom properties (defaults live in the CSS):
 *   --foil-intensity   overlay opacity (0–1)
 *   --foil-angle       direction of the iridescent streaks
 *   --foil-position-x  horizontal center of the glare highlight
 *   --foil-position-y  vertical center of the glare highlight
 * A future interactive ticket can drive these from JS for dynamic light effects.
 */
export const FoilCardImage: React.FC<FoilCardImageProps> = ({
  src,
  alt,
  foil = false,
  className = '',
  style = {},
  onError,
  loading = 'lazy',
  borderRadius = '8px',
}) => {
  return (
    <div
      className={`foil-card-image-wrapper ${className}`}
      style={{
        ...style,
        borderRadius,
      }}
    >
      <img
        src={src}
        alt={alt}
        className="foil-card-image"
        style={{ borderRadius }}
        onError={onError}
        loading={loading}
      />
      {foil && <div className="foil-card-overlay" aria-hidden="true" />}
    </div>
  );
};

export default FoilCardImage;
